import { styled } from '@mui/material/styles';

import { Theme, rem } from '../../theme';
import { ButtonBase } from '../Button';
import { Heading } from '../Heading';
import { CustomPicture } from '../ImageStandalone';
import { RichTextContent } from '../RichTextContent';

import { StoryBoxVariant } from './AddOnBox.types';

export const StyledWrapper = styled('article')<{
    $variant: StoryBoxVariant;
}>`
    ${({ theme, $variant }: { theme: Theme; $variant: StoryBoxVariant }) => `
        display: flex;
        flex: 1 1 30%;
        ${theme.breakpoints.up('desktop')} {
            flex-direction: row;
        }
        flex-direction: column;
        height: 100%;
        margin-bottom: ${theme.tggSpacing(rem(2), 8)};
        background-color: ${theme.palette.common.white};
        border: ${
            $variant === 'light blue'
                ? `none`
                : `${theme.tggSpacing(rem(0.2), 1)} solid ${theme.palette.common.lightGrey}`
        };
        border-radius: ${theme.tggSpacing(rem(1), 4)};
	`}
`;

export const StyledContent = styled('div')<{
    $vertical?: boolean;
}>`
    ${({ theme, $vertical }: { theme: Theme; $vertical?: boolean }) => `
        position: relative;
        display: flow;
        flex-grow: 1;
        flex-direction: row;
        /* stylelint-disable-next-line unit-allowed-list */
        padding: ${theme.tggSpacing([rem(2), rem(1.5)], [8, 6])};

        ${theme.breakpoints.up('desktop')} {
            display: flex;
            align-items: center;
            padding: ${theme.tggSpacing(rem(3), 12)};
        }
	`}
`;

export const StyledTagsList = styled('ul')`
    ${({ theme }: { theme: Theme }) => `
        display: flex;
        flex-wrap: wrap;
        padding: ${theme.tggSpacing(0, 0)};
        margin-bottom: ${theme.tggSpacing(rem(1), 4)};
        list-style: none;
	`}
`;

export const StyledTagLink = styled('a')`
    color: ${({ theme }: { theme: Theme }) => theme.palette.common.blue};
    text-decoration: none;
    text-transform: lowercase;
`;

export const StyledCustomPicture = styled(CustomPicture)`
    ${({ theme }: { theme: Theme }) => `
        height: ${theme.tggSpacing(rem(7.9), 32)};

        img {
            /* stylelint-disable-next-line unit-allowed-list */
            border-radius: ${theme.tggSpacing([rem(1), 0], [4, 0])};
        }

        ${theme.breakpoints.up('desktop')} {
            max-width: ${theme.tggSpacing(rem(26.4), 12)};
            height: ${theme.tggSpacing('auto', 'auto')};
            img {
                /* stylelint-disable-next-line unit-allowed-list */
                border-radius: ${theme.tggSpacing([rem(1), 0, 0, rem(1)], [4, 0, 0, 4])};
            }
        }
	`}
`;

export const StyledHeading = styled(Heading)`
    ${({ theme }: { theme: Theme }) =>
        theme.typography.text_19_bold_lh_22_desktop_21_lh_25};
    margin-bottom: ${({ theme }: { theme: Theme }) =>
        theme.tggSpacing(rem(1), 4)};
`;

export const StyledButton = styled(ButtonBase)`
    ${({ theme }: { theme: Theme }) => `
        width: 100%;
        /* stylelint-disable-next-line unit-allowed-list */
        padding: ${theme.tggSpacing([rem(0.2), rem(1)], [1, 4])};
        white-space: nowrap;

        ${theme.breakpoints.up('desktop')} {
            width: auto;
        }
	`}
`;

export const StyledPill = styled('div')<{
    $vertical?: boolean;
}>`
    ${({ theme }: { theme: Theme }) =>
        theme.typography.text_14_bold_desktop_18};

    ${({ theme }: { theme: Theme }) => `
        position: absolute;
        top: ${theme.tggSpacing(0, 0)};
        left: ${theme.tggSpacing(rem(1), 4)};
        /* stylelint-disable-next-line unit-allowed-list */
        padding: ${theme.tggSpacing([rem(0.3), rem(1)], [1.5, 4])};
        text-align: center;
        background-color: ${theme.palette.secondary.main};
        border-radius: ${theme.tggSpacing(rem(2), 8)};
        transform: translateY(-50%);

        ${theme.breakpoints.up('desktop')} {
            left: ${theme.tggSpacing(rem(-27), 67.5)};
        }
	`}
`;

export const StyledRichTextContent = styled(RichTextContent)`
    ${({ theme }: { theme: Theme }) => `
        padding: ${theme.tggSpacing(0, 0)};
        margin: ${theme.tggSpacing(0, 0)};

	`}

    p {
        ${({ theme }: { theme: Theme }) => theme.typography.bodySmall};
        ${({ theme }: { theme: Theme }) => `
            margin-bottom: ${theme.tggSpacing(rem(1), 2)};
        `};
    }

    li {
        list-style-type: disc;

        strong: {
            font-family: thegymgroupsans-headline;
        }
    }
`;

export const StyledButtonContainer = styled('div')`
    ${({ theme }: { theme: Theme }) => `
        display: grid;
        grid-template-columns: 1fr;
        gap: ${theme.tggSpacing(rem(2), 8)};
        margin-top: ${theme.tggSpacing('auto', 'auto')};

        /* stylelint-disable-next-line no-descending-specificity */
        ${theme.breakpoints.up('mobile')} {
            grid-template-columns: repeat(auto-fill, 14.2rem);

            &:has(a:only-child) {
                grid-template-columns: max-content;
            }
        }
	`}
`;

export const StyledAdditionalContent = styled('div')`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

export const StyledDescriptionContent = styled('div')`
    max-width: ${({ theme }: { theme: Theme }) =>
        theme.tggSpacing(rem(40), 160)};
`;
